import { useCallback, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonText,
  IonToolbar,
} from "@ionic/react";
import { close } from "ionicons/icons";
import { DateTime } from "luxon";
import { ShortenedDays } from "model";
import { Button, TimeInput } from "swing-components";

import { TimesTableRowType } from "~components/RequestForm/hooks";
import { translate } from "~utils";

import { UseBulkEditProps } from "./hooks";

type BulkEditTimesProps = UseBulkEditProps & {
  onDismiss: () => void;
};

export function BulkEditTimes(props: BulkEditTimesProps) {
  const { school, setIntervals, onDismiss } = props;

  const [allDays, setAllDays] = useState({
    start: DateTime.now().set({ hour: 7, minute: 30 }),
    end: DateTime.now().set({ hour: 16, minute: 30 }),
    isEnabled: false,
  });

  const daysShort: Array<ShortenedDays> = ["mon", "tue", "wed", "thu", "fri"];
  const days = [
    translate("EDIT_TIMES_MONDAYS"),
    translate("EDIT_TIMES_TUESDAYS"),
    translate("EDIT_TIMES_WEDNESDAYS"),
    translate("EDIT_TIMES_THURSDAYS"),
    translate("EDIT_TIMES_FRIDAYS"),
  ];

  const schoolHours = school.hours;

  const [bulkTimes, setBulkTimes] = useState(
    Array.from({ length: 5 }).map((_, index) => {
      const defaultHours = schoolHours.getByDay(daysShort[index]);
      const start = DateTime.now().set({
        hour: parseInt(defaultHours.start.toString().slice(0, -2)),
        minute: parseInt(defaultHours.start.toString().slice(-2)),
      });
      const end = DateTime.now().set({
        hour: parseInt(defaultHours.end.toString().slice(0, -2)),
        minute: parseInt(defaultHours.end.toString().slice(-2)),
      });

      return {
        start,
        end,
        isEnabled: false,
      };
    }),
  );

  const setSingleDayStartAt = useCallback((idx: number, dt: DateTime) => {
    setBulkTimes((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const endNeedsBump = dt.toMillis() >= row.end.toMillis();
        const newEnd = endNeedsBump ? dt.plus({ hours: 1 }) : row.end;
        return { isEnabled: true, start: dt, end: newEnd };
      }),
    );
  }, []);

  const setSingleDayEndAt = useCallback((idx: number, dt: DateTime) => {
    setBulkTimes((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const startNeedsPull = dt.toMillis() <= row.start.toMillis();
        const newStart = startNeedsPull ? dt.minus({ hours: 1 }) : row.start;
        return { isEnabled: Boolean(newStart && dt), end: dt, start: newStart };
      }),
    );
  }, []);

  const setAllDaysStartAt = (dt: DateTime) => {
    const endNeedsBump = allDays.end && dt.toMillis() >= allDays.end.toMillis();
    const newEnd = endNeedsBump ? dt.plus({ hours: 1 }) : allDays.end;
    if (dt) {
      // We want to set all the days if "All Days" gets updated.
      setBulkTimes((prev) =>
        prev.map((row) => {
          const endNeedsBump = dt.toMillis() >= row.end.toMillis();
          const newEnd = endNeedsBump ? dt.plus({ hours: 1 }) : row.end;
          return { ...row, start: dt, end: newEnd };
        }),
      );
    }
    setAllDays({ isEnabled: Boolean(dt && newEnd), start: dt, end: newEnd });
  };

  const setAllDaysEndAt = (dt: DateTime) => {
    const startNeedsPull = allDays.start && dt.toMillis() <= allDays.start.toMillis();
    const newStart = startNeedsPull ? dt.minus({ hours: 1 }) : allDays.start;
    if (dt) {
      // We want to set all the days if "All Days" gets updated.
      setBulkTimes((prev) =>
        prev.map((row) => {
          const startNeedsPull = dt.toMillis() <= row.end.toMillis();
          const newStart = startNeedsPull ? dt.minus({ hours: 1 }) : row.end;
          return { ...row, start: newStart, end: dt };
        }),
      );
    }
    setAllDays({ isEnabled: Boolean(newStart && dt), start: newStart, end: dt });
  };

  const isAllDaysCheckboxDisabled = () =>
    !allDays.start || !allDays.end || bulkTimes.some((item) => item.isEnabled);

  const isAllDaysTimeInputDisabled = bulkTimes.some((item) => item.isEnabled);

  const isSingleDayDisabled = allDays.isEnabled;

  const bulkUpdate = (item: TimesTableRowType) => {
    const index = daysShort.indexOf(item.startTime.weekdayShort.toLowerCase() as ShortenedDays);
    if (bulkTimes[index].isEnabled) {
      const newStart = item.startTime.set({
        hour: bulkTimes[index].start.hour,
        minute: bulkTimes[index].start.minute,
        second: bulkTimes[index].start.second,
      });
      const newEnd = item.endTime.set({
        hour: bulkTimes[index].end.hour,
        minute: bulkTimes[index].end.minute,
        second: bulkTimes[index].end.second,
      });
      return { ...item, startTime: newStart, endTime: newEnd };
    }
    return item;
  };

  const onSubmit = () => {
    setIntervals((old) =>
      old.map((item) => {
        if (allDays.isEnabled) {
          const newStart = item.startTime.set({
            hour: allDays.start.hour,
            minute: allDays.start.minute,
            second: allDays.start.second,
          });
          const newEnd = item.endTime.set({
            hour: allDays.end.hour,
            minute: allDays.end.minute,
            second: allDays.end.second,
          });
          return { ...item, startTime: newStart, endTime: newEnd };
        } else {
          return bulkUpdate(item);
        }
      }),
    );
  };

  const isSubmitDisabled = () => {
    if (bulkTimes.some((item) => item.isEnabled)) {
      return false;
    }
    if (allDays.isEnabled) {
      return false;
    }
    return true;
  };

  return (
    <IonContent>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonText slot="start">
            <h3 className="ion-padding-start">{translate("EDIT_TIMES_MODAL_HEADER")}</h3>
          </IonText>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={() => onDismiss()}>
              <IonIcon slot="icon-only" color="primary" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <div css={{ padding: 16 }}>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            padding: "16px 0",
            borderBottom: "solid 1px black",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <IonCheckbox
              checked={allDays.isEnabled}
              onIonChange={() =>
                setAllDays((old) => {
                  return { ...old, isEnabled: !old.isEnabled };
                })
              }
              labelPlacement="end"
              disabled={!(allDays.start && allDays.end) || isAllDaysCheckboxDisabled()}
            >
              {translate("EDIT_TIMES_ALL_DAYS")}
            </IonCheckbox>
          </div>
          <div css={{ display: "flex", gap: 4 }}>
            <TimeInput
              value={allDays.start}
              showTextInputOnDesktop
              onChange={(dt: DateTime) => setAllDaysStartAt(dt)}
              isDisabled={isAllDaysTimeInputDisabled}
            />
            -
            <TimeInput
              value={allDays.end}
              showTextInputOnDesktop
              onChange={(dt: DateTime) => setAllDaysEndAt(dt)}
              isDisabled={isAllDaysTimeInputDisabled}
            />
          </div>
        </div>

        {bulkTimes.map((item, idx) => {
          return (
            <div
              key={idx}
              css={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px 0",
                borderBottom: "solid 1px var(--swing-slate200)",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <IonCheckbox
                  checked={item.isEnabled}
                  onIonChange={() => {
                    setBulkTimes((prev) =>
                      prev.map((time, i) =>
                        i === idx ? { ...time, isEnabled: !time.isEnabled } : time,
                      ),
                    );
                  }}
                  labelPlacement="end"
                  disabled={isSingleDayDisabled || !(item.start && item.end)}
                >
                  {days[idx]}
                </IonCheckbox>
              </div>
              <div css={{ display: "flex", gap: 4 }}>
                <TimeInput
                  value={item.start}
                  showTextInputOnDesktop
                  onChange={(dt: DateTime) => setSingleDayStartAt(idx, dt)}
                  isDisabled={isSingleDayDisabled}
                />
                -
                <TimeInput
                  value={item.end}
                  showTextInputOnDesktop
                  onChange={(dt: DateTime) => setSingleDayEndAt(idx, dt)}
                  isDisabled={isSingleDayDisabled}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div css={{ position: "fixed", bottom: 0, padding: 16, width: "100%" }}>
        <Button
          onClick={() => {
            onSubmit();
            onDismiss();
          }}
          isFluid
          status={isSubmitDisabled() ? "disabled" : "enabled"}
        >
          {translate("LABEL_APPLY")}
        </Button>
      </div>
    </IonContent>
  );
}
